import React, { useContext, useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  Table,
  Badge,
  Button,
  Form,
  Modal,
} from "react-bootstrap";
import {
  Users,
  CheckCircle,
  FileText,
  CreditCard,
  Plus,
  Search,
  Eye,
  Edit2,
  X,
  QrCode,
} from "lucide-react";
import DistributorMasterLayout from "../../../layouts/distributor/DistributorMasterLayout";
import "../../../assets/styles/distributor/DistributorMyCustomers.css";
import axios from "axios";
import Swal from "sweetalert2";
import AppContext from "../../../context/AppContext";

const DistributorMyCustomers = () => {
  /* ─── Modal state ────────────────────────────── */
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [textForEdit, setTextForEdit] = useState("Add");
  const [editingCustomerId, setEditingCustomerId] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  /* ─── Form fields ────────────────────────────── */
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [cityId, setCityId] = useState("");
  const [areaId, setAreaId] = useState("");
  const [address, setAddress] = useState("");
  const [litersPerDay, setLitersPerDay] = useState("");
  const [pricePerLiter, setPricePerLiter] = useState("");

  /* ─── Data ───────────────────────────────────── */
  const [customers, setCustomers] = useState([]);
  const [cities, setCities] = useState([]);
  const [areas, setAreas] = useState([]);
  const [distributorId, setDistributorId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("All Status");

  const { baseUrl } = useContext(AppContext);

  /* ─── Stats (static) ─────────────────────────── */
  const statsData = [
    {
      id: 1,
      title: "Total Customers",
      value: customers.length.toString(),
      icon: Users,
      iconBg: "#dbeafe",
      iconColor: "#2563eb",
    },
    {
      id: 2,
      title: "Active",
      value: customers.filter((c) => c.status === "active").length.toString(),
      icon: CheckCircle,
      iconBg: "#d1fae5",
      iconColor: "#059669",
    },
    {
      id: 3,
      title: "Pending Requests",
      value: customers.filter((c) => c.status === "pending").length.toString(),
      icon: FileText,
      iconBg: "#fef3c7",
      iconColor: "#f59e0b",
    },
    {
      id: 4,
      title: "Total Dues",
      value: "PKR 14,200",
      icon: CreditCard,
      iconBg: "#fee2e2",
      iconColor: "#dc2626",
    },
  ];

  /* ══════════════════════════════════════════════
     RESET FORM FIELDS
  ══════════════════════════════════════════════ */
  const resetForm = () => {
    setFullName("");
    setEmail("");
    setPassword("");
    setPhoneNumber("");
    setCityId(cities.length > 0 ? cities[0].id : "");
    setAreaId("");
    setAddress("");
    setLitersPerDay("");
    setPricePerLiter("");
    setEditingCustomerId(null);
    setTextForEdit("Add");
  };

  /* ══════════════════════════════════════════════
     MODAL HANDLERS
  ══════════════════════════════════════════════ */

  /* Open modal for ADD */
  const handleShowModal = () => {
    resetForm();
    setShowModal(true);
  };

  /* Close modal */
  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  /* Open modal for EDIT — pre-fill all fields */
  const editCustomer = (customer) => {
    setTextForEdit("Edit");
    setEditingCustomerId(customer.id);
    setFullName(customer.mwcustomer.fullName || "");
    setEmail(customer.mwcustomer.email || "");
    setPassword(""); // leave blank
    setPhoneNumber(customer.mwcustomer.phonenumber || "");
    setCityId(customer.mwcustomer.mwcity.name || customer.cityId || "");
    setAreaId(customer.mwcustomer.mwarea.name || customer.areaId || "");
    setAddress(customer.address || "");
    setLitersPerDay(customer.mwcustomer.daily_requirement || "");
    setPricePerLiter(customer.pricePerLiter || customer.price || "");
    /* Load areas for the customer's city */
    if (customer.mwcity_id || customer.cityId) {
      getAreas(customer.mwcity_id || customer.cityId);
    }
    setShowModal(true);
  };

  /* Details modal */
  const handleShowDetails = (customer) => {
    setSelectedCustomer(customer);
    setShowDetailsModal(true);
  };
  const handleCloseDetails = () => {
    setShowDetailsModal(false);
    setSelectedCustomer(null);
  };

  /* ══════════════════════════════════════════════
     SAVE HANDLER  (Add + Edit in one)
  ══════════════════════════════════════════════ */
  const handleSave = (e) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append("fullName", fullName);
    formData.append("email", email);
    formData.append("phonenumber", phoneNumber);
    formData.append("mwcity_id", cityId);
    formData.append("mwarea_id", areaId);
    formData.append("mwdistributor_id", distributorId);
    formData.append("price", pricePerLiter);
    formData.append("daily_requirement", litersPerDay);
    formData.append("address", address);
    if (password) formData.append("password", password);

    if (textForEdit === "Edit") {
      /* ── UPDATE ── */
      formData.append("mwcustomer_id", editingCustomerId);
      formData.append("price", pricePerLiter);
      formData.append("daily_requirement", litersPerDay);

      axios
        .post(
          `${baseUrl}/distributor/distributorcustomer/updateCustomer`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } },
        )
        .then((response) => {
          console.log("updateCustomer:", response.data);
          if (response.data.success == true) {
            Swal.fire({
              title: "Updated!",
              text: "Customer updated successfully",
              icon: "success",
              confirmButtonText: "OK",
            });
            handleCloseModal();
            viewAllCustomers();
          } else {
            Swal.fire({
              title: "Error!",
              text: response.data.message || "Could not update customer",
              icon: "error",
              confirmButtonText: "OK",
            });
          }
        })
        .catch((error) => {
          console.log("updateCustomer error:", error);
          Swal.fire({
            title: "Error!",
            text: error?.response?.data?.message || "Could not update customer",
            icon: "error",
            confirmButtonText: "OK",
          });
        });
    } else {
      /* ── CREATE ── */
      axios
        .post(
          `${baseUrl}/distributor/distributorcustomer/addCustomer`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } },
        )
        .then((response) => {
          console.log("addCustomer:", response.data);
          if (response.data.success) {
            Swal.fire({
              title: "Congratulations!",
              text: "Customer added successfully",
              icon: "success",
              confirmButtonText: "Cool",
            });
            handleCloseModal();
            viewAllCustomers();
          } else {
            Swal.fire({
              title: "Error!",
              text: response.data.message || "Could not add customer",
              icon: "error",
              confirmButtonText: "Try Again",
            });
          }
        })
        .catch((error) => {
          console.log("addCustomer error:", error);
          Swal.fire({
            title: "Error!",
            text: error?.response?.data?.message || "Could not add customer",
            icon: "error",
            confirmButtonText: "Try Again",
          });
        });
    }
  };

  /* ══════════════════════════════════════════════
     API FUNCTIONS
  ══════════════════════════════════════════════ */

  /* Fetch all customers */
  const viewAllCustomers = () => {
    const id = localStorage.getItem("distributorId");
    const formData = new FormData();
    formData.append("mwdistributor_id", id);

    axios
      .post(
        `${baseUrl}/distributor/distributorcustomer/viewAllCustomers`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      )
      .then((response) => {
        console.log("viewAllCustomers:", response.data);
        /* Handle both response shapes: .customers array or .success + .customers */
        const list = response.data?.customers || response.data?.data || [];
        setCustomers(Array.isArray(list) ? list : []);
      })
      .catch((error) => {
        console.log("viewAllCustomers error:", error);
      });
  };

  /* Fetch all cities */
  const getAllCities = () => {
    axios
      .get(`${baseUrl}/distributor/cities/getAllCities`)
      .then((response) => {
        const citiesList = response.data?.cities || [];
        if (citiesList.length > 0) {
          setCities(citiesList);
          setCityId(citiesList[0].id);
          getAreas(citiesList[0].id);
        }
      })
      .catch((error) => console.log("getAllCities error:", error));
  };

  /* Fetch areas for selected city */
  const getAreas = (selectedCityId) => {
    setAreas([]);
    const formData = new FormData();
    formData.append("mwcity_id", selectedCityId);

    axios
      .post(`${baseUrl}/distributor/areas/getAreas`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        const areasList = response.data?.areas || [];
        setAreas(areasList);
        if (areasList.length > 0) setAreaId(areasList[0].id);
      })
      .catch((error) => {
        console.log("getAreas error:", error);
        setAreas([]);
      });
  };

  /* ── On mount ── */
  useEffect(() => {
    const id = localStorage.getItem("distributorId");
    setDistributorId(id);
    viewAllCustomers();
    getAllCities();
  }, []);

  /* ══════════════════════════════════════════════
     HELPERS
  ══════════════════════════════════════════════ */
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "active":
        return "bg-success";
      case "pending":
        return "bg-warning text-dark";
      case "suspended":
        return "bg-danger";
      default:
        return "bg-secondary";
    }
  };

  /* Filtered list */
  const filteredCustomers = customers.filter((c) => {
    const q = searchQuery.toLowerCase();
    const matchSearch =
      (c.name || "").toLowerCase().includes(q) || (c.phone || "").includes(q);
    const matchStatus =
      selectedStatus === "All Status" ||
      (c.status || "").toLowerCase() === selectedStatus.toLowerCase();
    return matchSearch && matchStatus;
  });

  /* ══════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════ */
  return (
    <DistributorMasterLayout>
      <div className="distributor-mycustomers-wrapper">
        {/* ── Header ── */}
        <div className="distributor-mycustomers-header">
          <div>
            <h1 className="distributor-mycustomers-title">My Customers</h1>
            <p className="distributor-mycustomers-subtitle">
              Manage your customer subscriptions and deliveries
            </p>
          </div>
          <Button
            className="distributor-mycustomers-add-btn"
            onClick={handleShowModal}
          >
            <Plus size={18} strokeWidth={2.5} /> Add Customer
          </Button>
        </div>

        {/* ── Stats ── */}
        <Row className="distributor-mycustomers-stats-row g-3 g-lg-4">
          {statsData.map((stat, index) => (
            <Col xs={12} sm={6} xl={3} key={stat.id}>
              <Card
                className="distributor-mycustomers-stat-card"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <Card.Body>
                  <div className="distributor-mycustomers-stat-content">
                    <div>
                      <span className="distributor-mycustomers-stat-label">
                        {stat.title}
                      </span>
                      <h3 className="distributor-mycustomers-stat-value">
                        {stat.value}
                      </h3>
                    </div>
                    <div
                      className="distributor-mycustomers-stat-icon-box"
                      style={{ backgroundColor: stat.iconBg }}
                    >
                      <stat.icon
                        size={24}
                        style={{ color: stat.iconColor }}
                        strokeWidth={2}
                      />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* ── Search + Filter ── */}
        <Card className="distributor-mycustomers-filter-card">
          <Card.Body>
            <Row className="g-3 align-items-center">
              <Col xs={12} lg={8}>
                <div className="distributor-mycustomers-search-wrapper">
                  <Search
                    className="distributor-mycustomers-search-icon"
                    size={20}
                  />
                  <Form.Control
                    type="text"
                    placeholder="Search by name or phone..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="distributor-mycustomers-search-input"
                  />
                </div>
              </Col>
              <Col xs={12} lg={4}>
                <Form.Select
                  className="distributor-mycustomers-status-filter"
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                >
                  <option>All Status</option>
                  <option>Active</option>
                  <option>Pending</option>
                  <option>Suspended</option>
                </Form.Select>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* ── Table ── */}
        <Card className="distributor-mycustomers-table-card">
          <Card.Body>
            <div className="distributor-mycustomers-table-wrapper">
              <Table responsive className="distributor-mycustomers-table">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Phone</th>
                    <th>Address</th>
                    <th>Liters/Day</th>
                    <th>Price/L</th>
                    <th>Dues</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCustomers.length > 0 ? (
                    // filteredCustomers.map((customer, index) => (
                    customers.map((customer, index) => (
                      <tr
                        key={customer.id}
                        style={{ animationDelay: `${index * 0.05}s` }}
                      >
                        <td>
                          <span className="distributor-mycustomers-name">
                            {customer.mwcustomer.fullName}
                          </span>
                        </td>
                        <td>
                          <span className="distributor-mycustomers-phone">
                            {customer.mwcustomer.phonenumber}
                          </span>
                        </td>
                        <td>
                          <span className="distributor-mycustomers-address">
                            {customer.mwcustomer.mwcity.name},{" "}
                            {customer.mwcustomer.mwarea.name}
                          </span>
                        </td>
                        <td>
                          <span className="distributor-mycustomers-liters">
                            {/* {customer.litersPerDay || customer.mwcustomer.daily_requirement} */}
                            {customer.daily_requirement}
                          </span>
                        </td>
                        <td>
                          <span className="distributor-mycustomers-price">
                            {customer.pricePerLiter || customer.price}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`distributor-mycustomers-dues ${
                              customer.dues && customer.dues !== "PKR 0"
                                ? "distributor-mycustomers-dues-red"
                                : ""
                            }`}
                          >
                            {customer.dues || "PKR 0"}
                          </span>
                        </td>
                        <td>
                          <Badge
                            className={`distributor-mycustomers-status-badge ${getStatusBadgeClass(customer.status)}`}
                          >
                            {customer.status}
                          </Badge>
                        </td>
                        <td>
                          {/* ── Only View + Edit — Suspend removed ── */}
                          <div className="distributor-mycustomers-actions">
                            <button
                              className="distributor-mycustomers-icon-btn"
                              title="View details"
                              onClick={() => handleShowDetails(customer)}
                            >
                              <Eye size={18} />
                            </button>
                            <button
                              className="distributor-mycustomers-icon-btn"
                              title="Edit customer"
                              onClick={() => editCustomer(customer)}
                            >
                              <Edit2 size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} className="text-center py-4 text-muted">
                        No customers found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>

        {/* ══════════════════════════════════════════
            ADD / EDIT MODAL
        ══════════════════════════════════════════ */}
        <Modal
          show={showModal}
          onHide={handleCloseModal}
          centered
          size="lg"
          className="distributor-mycustomers-modal"
        >
          <Modal.Header
            closeButton
            className="distributor-mycustomers-modal-header"
          >
            <Modal.Title className="distributor-mycustomers-modal-title">
              {textForEdit === "Edit" ? "Edit Customer" : "Add New Customer"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="distributor-mycustomers-modal-body">
            <p className="distributor-mycustomers-modal-subtitle">
              {textForEdit === "Edit"
                ? "Update delivery and pricing details"
                : "Add a customer and generate their QR code. An account will be created for them."}
            </p>

            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="distributor-mycustomers-form-group">
                    <Form.Label>Full Name</Form.Label>
                    <Form.Control
                      type="text"
                      disabled={textForEdit == "Edit"}
                      placeholder="Customer name"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="distributor-mycustomers-form-group">
                    <Form.Label>Phone Number</Form.Label>
                    <Form.Control
                      type="tel"
                      disabled={textForEdit == "Edit"}
                      placeholder="03XX-XXXXXXX"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="distributor-mycustomers-form-group">
                    <Form.Label>Email (Optional)</Form.Label>
                    <Form.Control
                      type="email"
                      disabled={textForEdit == "Edit"}
                      placeholder="customer@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="distributor-mycustomers-form-group">
                    <Form.Label>
                      Password
                      {/* Password{" "}
                      {textForEdit === "Edit" && (
                        <span style={{ fontWeight: 400, color: "#9ca3af", fontSize: "12px" }}>
                          (leave blank to keep current)
                        </span>
                      )} */}
                    </Form.Label>
                    <Form.Control
                      type="password"
                      disabled={textForEdit == "Edit"}
                      placeholder={
                        textForEdit === "Edit" ? "********" : "Account password"
                      }
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required={textForEdit === "Add"}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="distributor-mycustomers-form-group">
                    <Form.Label>City</Form.Label>
                    {textForEdit == "Edit" ? (
                      <Form.Control
                        type="text"
                        disabled={textForEdit == "Edit"}
                        value={cityId}
                      />
                    ) : (
                      <Form.Select
                        value={cityId || ""}
                        onChange={(e) => {
                          setCityId(e.target.value);
                          getAreas(e.target.value);
                        }}
                      >
                        {cities.map((city) => (
                          <option key={city.id} value={city.id}>
                            {city.name}
                          </option>
                        ))}
                      </Form.Select>
                    )}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="distributor-mycustomers-form-group">
                    <Form.Label>Area</Form.Label>
                    {textForEdit == "Edit" ? (
                      <Form.Control
                        type="text"
                        disabled={textForEdit == "Edit"}
                        value={areaId}
                      />
                    ) : areas.length > 0 ? (
                      <Form.Select
                        value={areaId}
                        onChange={(e) => setAreaId(e.target.value)}
                      >
                        <option value="">Select Area</option>
                        {areas.map((area) => (
                          <option key={area.id} value={area.id}>
                            {area.name}
                          </option>
                        ))}
                      </Form.Select>
                    ) : (
                      <Form.Select disabled>
                        <option>Area Not Found</option>
                      </Form.Select>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="distributor-mycustomers-form-group">
                <Form.Label>Complete Address</Form.Label>
                <Form.Control
                  type="text"
                  disabled={textForEdit == 'Edit'}
                  placeholder="House/Apt, Street, Block"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  required
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="distributor-mycustomers-form-group">
                    <Form.Label>Liters Per Day</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g. 2"
                      value={litersPerDay}
                      onChange={(e) => setLitersPerDay(e.target.value)}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="distributor-mycustomers-form-group">
                    <Form.Label>Price Per Liter (PKR)</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g. 180"
                      value={pricePerLiter}
                      onChange={(e) => setPricePerLiter(e.target.value)}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <div className="distributor-mycustomers-modal-footer">
                <Button variant="outline-secondary" onClick={handleCloseModal}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  className="distributor-mycustomers-submit-btn"
                  onClick={handleSave}
                >
                  {textForEdit === "Edit"
                    ? "Save Changes"
                    : "Add & Generate QR"}
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>

        {/* ══════════════════════════════════════════
            CUSTOMER DETAILS MODAL
        ══════════════════════════════════════════ */}
        <Modal
          show={showDetailsModal}
          onHide={handleCloseDetails}
          centered
          className="distributor-mycustomers-modal"
        >
          <Modal.Header
            closeButton
            className="distributor-mycustomers-modal-header"
          >
            <Modal.Title className="distributor-mycustomers-modal-title">
              Customer Details
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="distributor-mycustomers-modal-body">
            {selectedCustomer && (
              <>
                {/* QR Section */}
                <div className="distributor-mycustomers-qr-section">
                  <div className="distributor-mycustomers-qr-box">
                    <QrCode size={64} color="#4caf50" />
                  </div>
                  <p className="distributor-mycustomers-qr-code">
                    {selectedCustomer.qrCode ||
                      selectedCustomer.qr_code ||
                      "N/A"}
                  </p>
                </div>

                {/* Detail grid */}
                <div className="distributor-mycustomers-details-grid">
                  {[
                    ["Name", selectedCustomer.mwcustomer.fullName],
                    ["Phone", selectedCustomer.mwcustomer.phonenumber],
                    ["City", selectedCustomer.mwcustomer.mwcity.name],
                    ["Area", selectedCustomer.mwcustomer.mwarea.name],
                    [
                      "Liters/Day",
                      selectedCustomer.mwcustomer.daily_requirement,
                    ],
                    [
                      "Price/L",
                      selectedCustomer.pricePerLiter || selectedCustomer.price,
                    ],
                    ["Dues", selectedCustomer.dues || "PKR 0"],
                    [
                      "Joined",
                      selectedCustomer.joined || selectedCustomer.created_at,
                    ],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      className="distributor-mycustomers-detail-item"
                    >
                      <span className="distributor-mycustomers-detail-label">
                        {label}:
                      </span>
                      <span className="distributor-mycustomers-detail-value">
                        {value}
                      </span>
                    </div>
                  ))}
                  <div className="distributor-mycustomers-detail-item">
                    <span className="distributor-mycustomers-detail-label">
                      Status:
                    </span>
                    <Badge
                      className={getStatusBadgeClass(selectedCustomer.status)}
                    >
                      {selectedCustomer.status}
                    </Badge>
                  </div>
                </div>

                {/* Recent Deliveries */}
                {/* <div className="distributor-mycustomers-deliveries-section">
                  <h5 className="distributor-mycustomers-deliveries-title">Recent Deliveries</h5>
                  {selectedCustomer.recentDeliveries?.length > 0 ? (
                    selectedCustomer.recentDeliveries.map((d, i) => (
                      <div key={i} className="distributor-mycustomers-delivery-item">
                        <span>{d.date} — {d.liters} ✓</span>
                      </div>
                    ))
                  ) : (
                    <p className="distributor-mycustomers-no-deliveries">No recent deliveries</p>
                  )}
                </div> */}

                <div className="distributor-mycustomers-modal-footer">
                  <Button
                    variant="outline-secondary"
                    onClick={handleCloseDetails}
                  >
                    Close
                  </Button>
                </div>
              </>
            )}
          </Modal.Body>
        </Modal>
      </div>
    </DistributorMasterLayout>
  );
};

export default DistributorMyCustomers;
